import { createFileRoute, redirect } from '@tanstack/react-router';
import { getDefaultStore } from 'jotai';

import { getFcmTokenSync } from '@/features/notifications/atoms/fcmToken';
import { getRegisteredDevicesOptions } from '@/features/notifications/hooks/useGetRegisteredDevices/getRegisteredDevices.options';
import { checkNativePermissions } from '@/features/notifications/utils/nativePush';
import { isPushSupported } from '@/features/notifications/utils/pushNotifications';
import {
  setStepDataAtom,
  setStepDoneAtom,
} from '@/features/onboarding/atoms/steps';
import { NotificationsStep } from '@/features/onboarding/components/steps/notifications/notificationsStep';
import { getNextStep, getPrevStep } from '@/features/onboarding/utils/steps';
import { isNativePlatform } from '@/shared/utils/platform';

export const Route = createFileRoute(
  '/_protected/onboarding/_steps/notifications',
)({
  component: RouteComponent,
  beforeLoad: ({ location }) => {
    const state = location.state;

    // Skip notifications step only if push is NOT supported on this platform
    // (native platforms DO support push via Capacitor)
    if (!isPushSupported()) {
      const nextStep =
        'isStepBack' in state
          ? getPrevStep('notifications')
          : getNextStep('notifications');
      if (nextStep) {
        const store = getDefaultStore();
        store.set(setStepDoneAtom, {
          step: 'notifications',
          value: true,
        });
        throw redirect({
          to: `/onboarding/${nextStep}`,
          replace: true,
        });
      }
    }
  },
  loader: async ({ context }) => {
    // Check permission status based on platform
    let hasPermission = false;

    if (isNativePlatform()) {
      hasPermission = await checkNativePermissions();
    } else {
      hasPermission = Notification.permission === 'granted';
    }

    if (!hasPermission) {
      return;
    }

    const token = getFcmTokenSync();
    if (!token) {
      return;
    }
    const { devices } = await context.queryClient.ensureQueryData(
      getRegisteredDevicesOptions(),
    );
    const alreadyRegisteredToken = devices.some(d => d.token === token);
    // If the token is already registered, mark notifications enabled is true
    if (alreadyRegisteredToken) {
      const store = getDefaultStore();
      store.set(setStepDataAtom, {
        step: 'notifications',
        data: true,
      });
    }
  },
});

function RouteComponent() {
  return <NotificationsStep />;
}
